import _ from 'lodash'
import { StoreExt } from '@utils/reactExt'
import {
  Schema$UserPick,
  Schema$UserPickCreate,
  Schema$UserPickExtend,
  Schema$UserPickMarket,
  Schema$UserPickMatch,
  Schema$UserPickMatchCountPerSports,
  Schema$UserPickRecentHitRate,
  Schema$UserPickRecentMatchPackage,
  Schema$UserPickUserInfo,
  Schema$UserPickWayRatio,
} from '@interface/user-pick'
import { action, observable, runInAction } from 'mobx'
import matchupStore from '@store/matchupStore'
import { USER_PICK_ORDER_TYPE, USER_PICK_SPECIFIC_TYPE } from '@v2/constants/user-pick'
import { isMobile } from 'react-device-detect'
import { IUserPickMatches, IUserPickMonthlyBestUserPick } from '@protocol/user-pick'
import { EXCEPTIONAL_UNIQUE_ID, FROM_VIEW_TYPE } from '@v2/constants/common'
import StorageUtils from '@utils/storage-utils'
import { ALL_MEMBER_USER_PICK_KEY } from '@constants/index'
import { Schema$UserComment } from '@interface/comment'
import { globalStore } from '@store/index'

export class UserPickStore extends StoreExt {

  @observable
  userPickHomeMatches: Schema$UserPickMatch[] = []

  @observable
  userPickMatches: Schema$UserPickMatch[] = []

  @observable
  todayBestUserPickCount: number = 0

  @observable
  userInfos: Schema$UserPickUserInfo[] = []

  @observable
  userPickMatchesHasMore = false

  @observable
  notStartedMatchCount = 0

  @observable
  startedMatchCount = 0

  @observable
  closedMatchCount = 0

  @observable
  favoriteMatchIds: number[] = []

  @observable
  userPickHomeMatchCountPerSports: Schema$UserPickMatchCountPerSports = {
    total: 0,
  }

  @observable
  userPicks: Schema$UserPick[] = []

  @observable
  userPickMatchWayRatio: Schema$UserPickWayRatio

  /**
   * 최근 10경기 적중률 데이터 맵. 키: 유저 아이디, 값: 적중률 데이터
   */
  userPickRecentHitRateMap: Record<string, Schema$UserPickRecentHitRate>

  @observable
  userPickMarkets: Schema$UserPickMarket[]

  @observable
  hasMoreUserPicks = false

  @observable
  selectedUserPickMatch: Schema$UserPickMatch

  @observable
  fetchUserPickMatchDetailPageNum = 0

  @observable
  fetchUserPickMatchesPageNum = 0

  @observable
  fetchUserPickFilteredMatchesPageNum = 0

  @observable
  fetchUserPickMatchDetailOrder = USER_PICK_ORDER_TYPE.RECOMMEND

  @observable
  selectedEtcMarket: Schema$UserPickMarket

  @observable
  filteredUserPickMatches: Schema$UserPickMatch[] = []

  @observable
  filteredUserPickMatchHasMore = false

  @observable
  filteredTargetSportId: number

  @observable
  userPickMine: Schema$UserPickExtend[] = []

  @observable
  userPickMineHasMore = false

  @observable
  userPickMinePageNum = 0

  @observable
  userPickMineTotalCount = 0

  @observable
  userPickMyLikedList: Schema$UserPickExtend[] = []

  @observable
  userPickMyLikedListPageNum = 0

  @observable
  userPickMyLikedListHasMore = false

  @observable
  userPickMyLikedListTotalCount = 0

  /**
   * 나도 승부사 분석글 필터.
   * 분석글은 별도 페이지가 아닌 팝업으로 띄우는데
   * 기존 LeagueTab 에서 matchupStore 에 majorUniqueID 의 설정을 사용 하였으나
   * 이럴 경우 나도 승부사 매치 목록에 영향을 주므로 별도로 설정함.
   *
   * 승(무)패, 언더오버, 핸디캡, 기타로 구성된다.
   * 기타는 기타 전체 혹은 marketType 값이 설정될 수 있다.
   */
  @observable
  userPickMarketFilter: number

  @observable
  userPickSpecialMemberMatches: Schema$UserPickMatch[] = []

  @observable
  userPickSpecialMemberMatchesPageNum = 0

  @observable
  userPickSpecialMemberMatchesHasMore = false

  @observable
  userPickRecentMatchPackage: Schema$UserPickRecentMatchPackage

  @observable
  userPickSpecificMemberPicks: Schema$UserPickExtend[] = []

  @observable
  userPickSpecificMemberPickHasMore = false

  @observable
  userPickSpecificMemberPickPageNum = 0

  @observable
  userPickSpecificMemberPickCountMap: Record<USER_PICK_SPECIFIC_TYPE, number> = {
    [USER_PICK_SPECIFIC_TYPE.TOTAL]: 0,
    [USER_PICK_SPECIFIC_TYPE.HIT_PICK]: 0,
    [USER_PICK_SPECIFIC_TYPE.BEST_PICK]: 0,
  }

  @observable
  currentSportTab: number

  @observable
  currentSportsKey: string

  @observable
  currentSportId: number

  @observable
  monthlyBestUserPickUserInfos: Schema$UserPickUserInfo[] = []

  @observable
  monthlyBestUserPicks: Schema$UserPickExtend[] = []

  @observable
  totalMonthlyBestUserPickCount = 0

  @observable
  monthlyBestUserPickPageNum = 0

  @observable
  monthlyBestUserPickHasMore = false

  @observable
  lastBestUserPickId = 0

  @observable
  isNewBestPick = false

  @observable
  activeUserFilter = undefined

  isFetchUserPicksLoading = false

  @action
  resetAll(): void {
    this.resetMatches()
    this.resetMatchDetail()
    this.resetUserPickMine()
    this.resetUserPickMyLikedList()
    this.resetFilteredMatches()
    this.resetHomeMatches()
  }

  @action
  resetMatches(resetAll = true): void {
    this.userPickMatches = []
    this.userPickMatchesHasMore = false
    this.fetchUserPickMatchesPageNum = 0
    this.notStartedMatchCount = 0
    this.startedMatchCount = 0
    this.closedMatchCount = 0
    if (resetAll) {
      this.favoriteMatchIds = []
      this.userInfos = []
      matchupStore.setTournamentFilterList(undefined)
    }
  }

  @action
  resetMatchDetail(resetAll = true): void {
    this.userPicks = []
    this.fetchUserPickMatchDetailPageNum = 0
    this.hasMoreUserPicks = false
    this.fetchUserPickMatchDetailOrder = USER_PICK_ORDER_TYPE.RECOMMEND
    this.activeUserFilter = undefined
    this.userPickMarketFilter = undefined

    if (resetAll) {
      this.userPickMarkets = undefined
      this.userPickMatchWayRatio = undefined
      this.selectedEtcMarket = undefined
    }
  }

  @action
  resetHomeMatches(): void {
    this.userPickHomeMatches = []
    this.userPickHomeMatchCountPerSports = {
      total: 0,
    }
  }

  @action
  async fetchUserPickHomePackage(): Promise<void> {
    try {
      if (_.isNil(this.currentSportId)) {
        return
      }
      const sportId = this.currentSportId
      const result = await this.api.userPick.fetchHomePackage({ sportId })
      runInAction(() => {
        this.userPickHomeMatches = result.pickMatches
        this.userPickHomeMatchCountPerSports = result.userPickCountPerSports
        this.todayBestUserPickCount = result.todayBestUserPickCount
      })
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * 나도 승부사 페이지에서 요청하는 분석글 패키지
   *
   * 매치정보, S멤버 정보, 시작전 매치 개수, 찜매치 들을 가지고 온다.
   */
  @action
  async fetchUserPickMatchesPackage(): Promise<void> {
    try {
      const sportId = this.filteredTargetSportId ?? matchupStore.currentSportId
      const uniqueFilter = matchupStore.majorUniqueID
      if (_.isNil(sportId) || _.isNil(uniqueFilter)) {
        return
      }

      const result = await this.api.userPick.fetchMatchPackage({
        sportId, uniqueFilter,
      })
      runInAction(() => {
        this.userPickMatches = result.pickMatches
        this.userPickMatchesHasMore = result.hasMore
        this.userInfos = result.userInfos
        this.notStartedMatchCount = result.notStartedMatchCount
        this.startedMatchCount = result.startedMatchCount
        this.closedMatchCount = result.closedMatchCount
        this.favoriteMatchIds = result.favoriteMatchIds
        matchupStore.setMatchupFilters(result.leagueFilters)

        if (isMobile) {
          this.userPickHomeMatchCountPerSports = undefined
        }

        if (result.hasMore) {
          this.fetchUserPickMatchesPageNum = 1
        }
      })
    } catch (err) {
      console.error(err)
    }
  }

  @action
  async fetchUserPickMatches(selectedUserPromiseIds: string[], reset = false): Promise<void> {
    try {
      if (reset) {
        this.resetMatches(false)
      }
      const sportId = this.filteredTargetSportId ?? matchupStore.currentSportId
      const uniqueFilter = matchupStore.majorUniqueID
      if (_.isNil(sportId) || _.isNil(uniqueFilter)) {
        return
      }

      const params: IUserPickMatches.Params = {
        sportId,
        uniqueFilter,
        pageNum: this.fetchUserPickMatchesPageNum,
      }

      if (!_.isEmpty(selectedUserPromiseIds) && !selectedUserPromiseIds.includes(ALL_MEMBER_USER_PICK_KEY)) {
        params.selectedUserPromiseIds = selectedUserPromiseIds
      }

      const tournamentIds = matchupStore.tournamentFilterList ?? []
      if (!_.isEmpty(tournamentIds)) {
        params.tournamentIds = tournamentIds
      }

      const result = await this.api.userPick.fetchMatches(params)
      runInAction(() => {
        this.userPickMatches = this.userPickMatches.concat(result.pickMatches)
        this.userPickMatchesHasMore = result.hasMore
        if (result.hasMore) {
          this.fetchUserPickMatchesPageNum += 1
        }
      })
    } catch (err) {
      console.error(err)
    }
  }

  @action
  resetFilteredMatches(resetAll = true): void {
    this.filteredUserPickMatches = []
    this.filteredUserPickMatchHasMore = false
    this.fetchUserPickFilteredMatchesPageNum = 0
    if (resetAll) {
      this.filteredTargetSportId = undefined
    }
  }

  @action
  async fetchUserPickFilteredMatches(reset = false): Promise<void> {
    try {
      const tournamentIds = matchupStore.tournamentFilterList
      let uniqueFilter = matchupStore.majorUniqueID
      if (_.size(tournamentIds) === 0 && _.isNil(uniqueFilter)) {
        return
      }

      if (uniqueFilter === EXCEPTIONAL_UNIQUE_ID.FAVORITE_MATCH) {
        uniqueFilter = null
      }

      if (reset) {
        this.resetFilteredMatches(false)
      }

      const result = await this.api.userPick.fetchFilteredMatches({
        tournamentIds,
        pageNum: this.fetchUserPickFilteredMatchesPageNum,
        uniqueFilter,
      })
      runInAction(() => {
        this.filteredUserPickMatches = result.items
        this.filteredUserPickMatchHasMore = result.hasMore

        if (result.hasMore) {
          this.fetchUserPickFilteredMatchesPageNum += 1
        }
      })
    } catch (err) {
      console.error(err)
    }
  }

  @action
  async fetchUserPickSpecialMembers(): Promise<void> {
    try {
      const sportId = matchupStore.currentSportId
      const uniqueFilter = matchupStore.majorUniqueID
      if (_.isNil(uniqueFilter)) {
        return
      }
      const result = await this.api.userPick.fetchSpecialMembers({ sportId, uniqueFilter })
      runInAction(() => {
        this.userInfos = result.items
      })
    } catch (err) {
      console.error(err)
    }
  }

  @action
  async fetchUserPickLeagueFilters(): Promise<void> {
    try {
      const sportId = matchupStore.currentSportId
      const uniqueFilter = matchupStore.majorUniqueID
      if (_.isNil(uniqueFilter)) {
        return
      }
      const result = await this.api.userPick.fetchLeagueFilters({ sportId, uniqueFilter })
      runInAction(() => {
        matchupStore.setMatchupFilters(result.items)
      })
    } catch (err) {
      console.error(err)
    }
  }

  @action
  setFavoriteMatchIds(ids: number[]): void {
    this.favoriteMatchIds = ids
  }

  @action
  async fetchUserPickMatchDetailPackage(opts: {
    matchId: number
    reset?: boolean
    targetUserPromiseId?: string
    targetUserPickId?: string
  }): Promise<void> {
    try {
      if (!this.userPickMarketFilter) {
        return
      }

      const { matchId, reset, targetUserPromiseId, targetUserPickId } = opts
      if (reset) {
        runInAction(() => {
          this.fetchUserPickMatchDetailOrder = USER_PICK_ORDER_TYPE.RECOMMEND
          this.fetchUserPickMatchDetailPageNum = 0
        })
      }

      const params = {
        matchId,
        orderType: this.fetchUserPickMatchDetailOrder,
        marketFilter: Number(this.userPickMarketFilter),
        targetUserPromiseId,
        targetUserPickId,
      }

      if (this.selectedEtcMarket) {
        params.marketFilter = this.selectedEtcMarket.marketType
      }

      const result = await this.api.userPick.fetchUserPickMatchDetailPackage(params)
      runInAction(() => {
        this.userPicks = result.userPicks
        this.userPickMarkets = result.markets
        this.userPickMatchWayRatio = result.wayRatio
        this.hasMoreUserPicks = result.hasMore
        this.userPickRecentHitRateMap = result.recentHitRateMap
        if (this.selectedUserPickMatch && this.selectedUserPickMatch.matchId === matchId) {
          this.selectedUserPickMatch.isFavoriteMatch = result.isFavoriteMatch
        }
        if (this.userPickMatches) {
          const existsPickMatch = _.find(this.userPickMatches, { matchId })
          if (existsPickMatch) {
            existsPickMatch.isFavoriteMatch = result.isFavoriteMatch
          }
        }
        if (this.userPickHomeMatches) {
          const existsPickHomeMatch = _.find(this.userPickHomeMatches, { matchId })
          if (existsPickHomeMatch) {
            existsPickHomeMatch.isFavoriteMatch = result.isFavoriteMatch
          }
        }
        if (result.hasMore) {
          this.fetchUserPickMatchDetailPageNum += 1
        }
      })
    } catch (err) {
      console.error(err)
    }
  }

  @action
  setFetchUserPickMatchDetailPageNum(pageNum: number): void {
    this.fetchUserPickMatchDetailPageNum = pageNum
  }

  @action
  async createUserPick(data: Schema$UserPickCreate): Promise<boolean> {
    try {
      const result = await this.api.userPick.createUserPick({ data })

      if (result.result) {
        globalStore.addActivityScoreNoticeQueue(result.activityInfo)
      }
      return result.result
    } catch (err) {
      console.error(err)
    }
  }

  @action
  setSelectedUserPickMatch(userPickMatch: Schema$UserPickMatch): void {
    this.selectedUserPickMatch = userPickMatch
  }

  @action
  updateFavoriteMatchIds(matchId: number, isFavorite: boolean): void {
    if (isFavorite) {
      this.favoriteMatchIds = this.favoriteMatchIds.concat([matchId])
    } else {
      _.remove(this.favoriteMatchIds, mId => mId === matchId)
      this.favoriteMatchIds = [...this.favoriteMatchIds]
    }
  }

  @action
  async fetchMatch(matchId: number): Promise<void> {
    const result = await this.api.userPick.fetchMatch({ matchId })
    const { pickMatch } = result
    this.setSelectedUserPickMatch(pickMatch)
    this.setActiveUserFilter(true)
  }

  @action
  updateMatchFavorite(isFavoriteMatch: boolean, matchId: number, fromView?: FROM_VIEW_TYPE): void {
    let updated = false
    if (this.userPickMatches) {
      const exists = _.find(this.userPickMatches, { matchId })
      if (exists) {
        exists.isFavoriteMatch = isFavoriteMatch
        this.userPickMatches = [...this.userPickMatches]
        updated = true
      }
    }
    if (this.filteredUserPickMatches) {
      const exists = _.find(this.filteredUserPickMatches, { matchId })
      if (exists) {
        exists.isFavoriteMatch = isFavoriteMatch
        this.filteredUserPickMatches = [...this.filteredUserPickMatches]
        updated = true
      }
    }
    if (this.selectedUserPickMatch && this.selectedUserPickMatch.matchId === matchId) {
      this.selectedUserPickMatch.isFavoriteMatch = isFavoriteMatch
      updated = true
    }
    if (updated) {
      this.updateFavoriteMatchIds(matchId, isFavoriteMatch)
    }

    if (isFavoriteMatch) {
      matchupStore.increaseFavoriteTotalPreMatchCount()
    } else {
      matchupStore.decreaseFavoriteTotalPreMatchCount()
    }

    if (fromView !== FROM_VIEW_TYPE.MAIN_MATCHUP
      && Number(matchupStore.majorUniqueID) === EXCEPTIONAL_UNIQUE_ID.FAVORITE_MATCH) {
      this.fetchUserPickMatchesPackage().catch(console.error)
    }
  }

  @action
  setFetchUserPickMatchDetailOrder(order: USER_PICK_ORDER_TYPE): void {
    this.fetchUserPickMatchDetailOrder = order
  }

  @action
  incrementUserPickLikedCount(userPickId: number): void {
    if (!_.isEmpty(this.userPicks)) {
      const exists = _.find(this.userPicks, { id: userPickId })
      if (exists) {
        exists.likeCount += 1
        exists.isLiked = true
      }
    }
    if (!_.isEmpty(this.monthlyBestUserPicks)) {
      const exists = _.find(this.monthlyBestUserPicks, { id: userPickId })
      if (exists) {
        exists.likeCount += 1
        exists.isLiked = true
      }
    }
    if (!_.isEmpty(this.userPickSpecificMemberPicks)) {
      const exists = _.find(this.userPickSpecificMemberPicks, { id: userPickId })
      if (exists) {
        exists.likeCount += 1
        exists.isLiked = true
      }
    }
    if (!_.isEmpty(this.userPickMine)) {
      const exists = _.find(this.userPickMine, { id: userPickId })
      if (exists) {
        exists.likeCount += 1
        exists.isLiked = true
      }
    }
  }

  @action
  decrementUserPickLikedCount(userPickId: number): void {
    if (!_.isEmpty(this.userPicks)) {
      const exists = _.find(this.userPicks, { id: userPickId })
      if (exists) {
        if (exists.likeCount >= 0) {
          exists.likeCount -= 1
        }
        exists.isLiked = false
      }
    }
    if (!_.isEmpty(this.monthlyBestUserPicks)) {
      const exists = _.find(this.monthlyBestUserPicks, { id: userPickId })
      if (exists) {
        if (exists.likeCount >= 0) {
          exists.likeCount -= 1
        }
        exists.isLiked = false
      }
    }
    if (!_.isEmpty(this.userPickSpecificMemberPicks)) {
      const exists = _.find(this.userPickSpecificMemberPicks, { id: userPickId })
      if (exists) {
        if (exists.likeCount >= 0) {
          exists.likeCount -= 1
        }
        exists.isLiked = false
      }
    }
    if (!_.isEmpty(this.userPickMine)) {
      const exists = _.find(this.userPickMine, { id: userPickId })
      if (exists) {
        if (exists.likeCount >= 0) {
          exists.likeCount -= 1
        }
        exists.isLiked = false
      }
    }
  }

  @action
  async toggleUserPickLiked(userPickId: number): Promise<boolean> {
    try {
      const result = await this.api.userPick.toggleUserPickLike({ userPickId })

      runInAction(() => {
        if (result.isLiked) {
          this.incrementUserPickLikedCount(userPickId)
          globalStore.addActivityScoreNoticeQueue(result.activityInfo)
        } else {
          this.decrementUserPickLikedCount(userPickId)
        }
      })

      return result.isLiked
    } catch (err) {
      console.error(err)
    }
  }

  @action
  setSelectedEtcMarket(etcMarket: Schema$UserPickMarket): void {
    this.selectedEtcMarket = etcMarket
  }

  @action
  setFilteredTargetSportId(sportId: number): void {
    this.filteredTargetSportId = sportId
  }

  @action
  setUserPickMarketFilter(userPickFilter: number): void {
    this.userPickMarketFilter = userPickFilter
  }

  @action
  resetUserPickMine(): void {
    this.userPickMine = []
    this.userPickMinePageNum = 0
    this.userPickMineHasMore = false
    this.userPickMineTotalCount = 0
  }

  @action
  async fetchUserPickMine(reset = false, baseMonth: string): Promise<void> {
    try {
      if (reset) {
        this.resetUserPickMine()
      }
      const result = await this.api.my.getUserPicks({
        pageNum: this.userPickMinePageNum,
        baseMonth,
      })
      runInAction(() => {
        this.userPickMine = this.userPickMine.concat(result.items)
        this.userPickMineHasMore = result.hasMore
        this.userPickMineTotalCount = result.totalCount
        if (result.hasMore) {
          this.userPickMinePageNum += 1
        }
      })
    } catch (err) {
      console.error(err)
    }
  }

  @action
  resetUserPickMyLikedList(): void {
    this.userPickMyLikedList = []
    this.userPickMyLikedListPageNum = 0
    this.userPickMyLikedListHasMore = false
    this.userPickMyLikedListTotalCount = 0
  }

  @action
  async fetchUserPickMyLikedList(reset = false, baseMonth?: string): Promise<void> {
    try {
      if (reset) {
        this.resetUserPickMyLikedList()
      }
      const result = await this.api.my.getUserPickLikedList({
        pageNum: this.userPickMyLikedListPageNum,
        baseMonth,
      })
      runInAction(() => {
        this.userPickMyLikedList = this.userPickMyLikedList.concat(result.items)
        this.userPickMyLikedListHasMore = result.hasMore
        this.userPickMyLikedListTotalCount = result.totalCount
        if (result.hasMore) {
          this.userPickMyLikedListPageNum += 1
        }
      })
    } catch (err) {
      console.error(err)
    }
  }

  @action
  async fetchUserPickSpecificUsersMatches(selectedUserPromiseIds: string[], reset = false): Promise<void> {
    try {
      const sportId = this.filteredTargetSportId ?? matchupStore.currentSportId
      const uniqueFilter = matchupStore.majorUniqueID
      if (_.isNil(sportId) || _.isNil(uniqueFilter)) {
        return
      }

      if (_.isEmpty(selectedUserPromiseIds)) {
        return
      }

      let targetUserPromiseIds = []
      if (!selectedUserPromiseIds.includes(ALL_MEMBER_USER_PICK_KEY)) {
        targetUserPromiseIds = selectedUserPromiseIds
      }

      if (reset) {
        this.fetchUserPickMatchesPageNum = 0
      }

      const result = await this.api.userPick.fetchSpecificUsersMatches({
        targetUserPromiseIds: targetUserPromiseIds,
        pageNum: this.fetchUserPickMatchesPageNum,
        sportId,
        uniqueFilter,
      })

      runInAction(() => {
        this.userPickMatches = result.items
        this.userPickMatchesHasMore = result.hasMore
        if (result.hasMore) {
          this.fetchUserPickMatchesPageNum += 1
        }
      })
    } catch (err) {
      console.error(err)
    }
  }

  @action
  resetUserPickRecentMatchPackage(): void {
    this.userPickRecentMatchPackage = undefined
  }

  @action
  resetUserPickSpecificMemberPicks(resetAll = false): void {
    this.userPickSpecificMemberPicks = []
    this.userPickSpecificMemberPickHasMore = false
    this.userPickSpecificMemberPickPageNum = 0
    if (resetAll) {
      this.userPickSpecificMemberPickCountMap = {
        [USER_PICK_SPECIFIC_TYPE.TOTAL]: 0,
        [USER_PICK_SPECIFIC_TYPE.HIT_PICK]: 0,
        [USER_PICK_SPECIFIC_TYPE.BEST_PICK]: 0,
      }
    }
  }

  @action
  async fetchUserPickSpecificUserPackage(targetUserPromiseId: string): Promise<void> {
    try {
      this.resetUserPickRecentMatchPackage()
      this.resetUserPickSpecificMemberPicks()

      const result = await this.api.userPick.fetchSpecificUserPackage({ targetUserPromiseId })

      runInAction(() => {
        const data: Schema$UserPickRecentMatchPackage = {
          userInfo: result.userInfo,
          recentMatches: result.recentMatches,
          hitStateSuccessCount: result.hitStateSuccessCount,
          hitStateFailCount: result.hitStateFailCount,
        }
        this.userPickRecentMatchPackage = data
        this.userPickSpecificMemberPicks = this.userPickSpecificMemberPicks.concat(result.userPicks)
        this.userPickSpecificMemberPickHasMore = result.hasMore
        if (result.hasMore) {
          this.userPickSpecificMemberPickPageNum += 1
        }
        this.userPickSpecificMemberPickCountMap = result.pickCountMap
      })
    } catch (err) {
      console.error(err)
    }
  }

  @action
  async fetchUserPickSpecificMemberPicks(
    targetUserPromiseId: string, pickType: USER_PICK_SPECIFIC_TYPE, reset = false,
  ): Promise<void> {
    try {
      if (reset) {
        this.resetUserPickSpecificMemberPicks()
      }
      const result = await this.api.userPick.fetchSpecificMemberPicks({
        targetUserPromiseId,
        pageNum: this.userPickSpecificMemberPickPageNum,
        pickType,
      })
      runInAction(() => {
        this.userPickSpecificMemberPicks = this.userPickSpecificMemberPicks.concat(result.userPicks)
        this.userPickSpecificMemberPickHasMore = result.hasMore
        this.userPickSpecificMemberPickCountMap = result.pickCountMap
        if (result.hasMore) {
          this.userPickSpecificMemberPickPageNum += 1
        }
      })
    } catch (err) {
      console.error(err)
    }
  }

  @action
  async fetchUserPicks(matchId: number): Promise<void> {
    this.isFetchUserPicksLoading = true
    try {
      const params = {
        matchId,
        marketFilter: this.userPickMarketFilter,
        orderType: this.fetchUserPickMatchDetailOrder,
        pageNum: this.fetchUserPickMatchDetailPageNum,
      }
      const result = await this.api.userPick.fetchUserPicks(params)

      runInAction(() => {
        this.userPicks = this.userPicks.concat(result.userPicks)
        this.hasMoreUserPicks = result.hasMore
        if (_.isNil(this.userPickRecentHitRateMap)) {
          this.userPickRecentHitRateMap = result.recentHitRateMap
        } else {
          this.userPickRecentHitRateMap = { ...this.userPickRecentHitRateMap, ...result.recentHitRateMap }
        }

        if (result.hasMore) {
          this.fetchUserPickMatchDetailPageNum += 1
        }
      })
    } catch (err) {
      console.error(err)
    }
    this.isFetchUserPicksLoading = false
  }

  @action
  setSportTab(sportTab: number, subKey: string, sportId: number): void {
    this.currentSportTab = sportTab
    this.currentSportsKey = subKey
    this.currentSportId = sportId
  }

  @action
  resetMonthlyBestUserPicks(resetAll = true): void {
    this.monthlyBestUserPickHasMore = false
    this.monthlyBestUserPickPageNum = 0
    this.userPickRecentHitRateMap = undefined

    if (resetAll) {
      this.totalMonthlyBestUserPickCount = 0
      this.monthlyBestUserPickUserInfos = []
      this.monthlyBestUserPicks = []
    }
  }

  @action
  async fetchMonthlyBestUserPicks(
    baseMonth: string, selectedUserPromiseIds: string[], reset = false,
  ): Promise<void> {
    try {
      if (_.isEmpty(selectedUserPromiseIds)) {
        return
      }
      if (!baseMonth) {
        return
      }
      if (reset) {
        this.resetMonthlyBestUserPicks(false)
      }
      const params: IUserPickMonthlyBestUserPick.Params = {
        baseMonth,
        pageNum: this.monthlyBestUserPickPageNum,
      }

      if (!_.isEmpty(selectedUserPromiseIds)
        && !selectedUserPromiseIds.includes(ALL_MEMBER_USER_PICK_KEY)) {
        params.targetUserPromiseIds = selectedUserPromiseIds
      }
      const result = await this.api.userPick.fetchMonthlyBestUserPicks(params)
      runInAction(() => {
        this.monthlyBestUserPickUserInfos = result.userInfos
        this.totalMonthlyBestUserPickCount = result.totalCount
        if (_.isNil(this.userPickRecentHitRateMap)) {
          this.userPickRecentHitRateMap = result.recentHitRateMap
        } else {
          this.userPickRecentHitRateMap = { ...this.userPickRecentHitRateMap, ...result.recentHitRateMap }
        }
        if (reset) {
          this.monthlyBestUserPicks = result.userPicks
        } else {
          this.monthlyBestUserPicks = this.monthlyBestUserPicks.concat(result.userPicks)
        }
        this.monthlyBestUserPickHasMore = result.hasMore
        if (result.hasMore) {
          this.monthlyBestUserPickPageNum += 1
        }
        this.setLastBestUserPickId(result.lastBestUserPickId)
      })
    } catch (err) {
      console.error(err)
    }
  }

  @action
  async fetchLastBestUserPickId(): Promise<void> {
    try {
      const result = await this.api.userPick.fetchLastBestUserPickId()
      runInAction(() => {
        if (result) {
          this.setLastBestUserPickId(result.lastBestUserPickId)
        }
      })
    } catch (err) {
      console.error(err)
    }
  }

  @action
  setLastBestUserPickId(userPickId: number): void {
    this.lastBestUserPickId = userPickId
    this.updateIsNewBestPickNotice()
  }

  @action
  saveLastBestUserPickIdToLocal(): void {
    if (this.lastBestUserPickId) {
      StorageUtils.saveLastUserPickBestPickId(this.lastBestUserPickId)
    }
  }

  @action
  updateIsNewBestPickNotice(): void {
    if (!this.lastBestUserPickId) {
      this.isNewBestPick = false
    } else {
      const savedLastBestPickId = StorageUtils.getLastUserPickBestPickId()
      this.isNewBestPick = savedLastBestPickId !== this.lastBestUserPickId
    }
  }

  createCommentCallback = (comment: Schema$UserComment): void => {
    this.increaseUserPickCommentCount(comment)
    this.increaseSpecificUserPickCommentCount(comment)
    this.increaseMonthlyBestUserPickCommentCount(comment)
    this.increaseUserPickMineCommentCount(comment)
  }

  deleteCommentCallback = (comment: Schema$UserComment): void => {
    this.decreaseUserPickCommentCount(comment)
    this.decreaseSpecificUserPickCommentCount(comment)
    this.decreaseMyLikeUserPickCommentCount(comment)
    this.decreaseMonthlyBestUserPickCommentCount(comment)
    this.decreaseUserPickMineCommentCount(comment)
  }

  @action
  increaseUserPickCommentCount = (comment: Schema$UserComment): void => {
    const exists = _.find(this.userPicks, { id: Number(comment.targetId) })
    if (exists) {
      exists.commentCount += 1
    }
  }

  @action
  decreaseUserPickCommentCount = (comment: Schema$UserComment): void => {
    const exists = _.find(this.userPicks, { id: Number(comment.targetId) })
    if (exists) {
      exists.commentCount -= 1
    }
  }

  @action
  increaseSpecificUserPickCommentCount = (comment: Schema$UserComment): void => {
    const exists = _.find(this.userPickSpecificMemberPicks, { id: Number(comment.targetId) })
    if (exists) {
      exists.commentCount += 1
    }
  }

  @action
  decreaseSpecificUserPickCommentCount = (comment: Schema$UserComment): void => {
    const exists = _.find(this.userPickSpecificMemberPicks, { id: Number(comment.targetId) })
    if (exists) {
      exists.commentCount -= 1
    }
  }

  @action
  increaseMyLikeUserPickCommentCount = (comment: Schema$UserComment): void => {
    const exists = _.find(this.userPickMyLikedList, { id: Number(comment.targetId) })
    if (exists) {
      exists.commentCount += 1
    }
  }

  @action
  decreaseMyLikeUserPickCommentCount = (comment: Schema$UserComment): void => {
    const exists = _.find(this.userPickMyLikedList, { id: Number(comment.targetId) })
    if (exists) {
      exists.commentCount -= 1
    }
  }

  @action
  increaseMonthlyBestUserPickCommentCount = (comment: Schema$UserComment): void => {
    const exists = _.find(this.monthlyBestUserPicks, { id: Number(comment.targetId) })
    if (exists) {
      exists.commentCount += 1
    }
  }

  @action
  decreaseMonthlyBestUserPickCommentCount = (comment: Schema$UserComment): void => {
    const exists = _.find(this.monthlyBestUserPicks, { id: Number(comment.targetId) })
    if (exists) {
      exists.commentCount -= 1
    }
  }

  @action
  increaseUserPickMineCommentCount = (comment: Schema$UserComment): void => {
    const exists = _.find(this.userPickMine, { id: Number(comment.targetId) })
    if (exists) {
      exists.commentCount += 1
    }
  }

  @action
  decreaseUserPickMineCommentCount = (comment: Schema$UserComment): void => {
    const exists = _.find(this.userPickMine, { id: Number(comment.targetId) })
    if (exists) {
      exists.commentCount -= 1
    }
  }

  @action
  setUserBlock = (
    picks: Schema$UserPickExtend[],
    userPromiseId: string,
    isUserBlock = false,
    isPickBlock = false,
  ) => {
    _.forEach(picks, pick => {
      if (!_.eq(pick.pickOwner.userPromiseId, userPromiseId)) {
        return
      }

      pick.pickOwner.isBlocked = isUserBlock
      pick.isBlocked = isPickBlock
    })

    return _.concat([], picks)
  }

  @action
  setActiveUserFilter = (isActive: boolean): void => {
    this.activeUserFilter = isActive
  }
}

export default new UserPickStore()
